<script setup lang="ts">
import {
  computed,
  onUnmounted,
  ref,
  watch,
} from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import type {
  CreativeDomainNames,
  IProjectSettingsFormPayload,
  IProjectSettingsListItem,
} from '@modules/general-settings/submodules/project-settings/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useConfig } from '@modules/config/composables';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT,
} from '@modules/general-settings/submodules/project-settings/constants';
import { EProjectSettingsField } from '@modules/general-settings/submodules/project-settings/enums';
import useSettingsProjectSaveAllMutation
  from '@modules/general-settings/submodules/project-settings/mutations/useSettingsProjectSaveAllMutation';
import useSettingsProjectQuery
  from '@modules/general-settings/submodules/project-settings/queries/useSettingsProjectQuery';
import { useI18n, useLanguages } from '@modules/i18n/composables';
import { useMerchantStore } from '@modules/merchant/store';
import { isNullOrUndefined, isNumber } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

const { data: projectSettings, isLoading } = useSettingsProjectQuery();
const { mutateAsync: saveSettings, isLoading: isLoadingSubmission } = useSettingsProjectSaveAllMutation();

const { config } = useConfig();
const { updateThemeColors } = useMerchantStore();
const { interfaceLanguages, allLanguages, creativeLanguages } = useLanguages();
const { getTranslation } = useI18n();

const defaultLocaleSelection = ref<string>();
const interfaceLocaleSelection = ref<string[]>([]);
const creativeLocaleSelection = ref<string[]>([]);

function buildList(list: IProjectSettingsListItem[] = []): IFormSelectInputOption<number>[] {
  return list.map((a) => ({ value: a.id, label: a.name || a.value }));
}

const fallbackLocale = computed(() => config.value?.fallback_locale);
const serialOptions = computed<IFormSelectInputOption<number>[]>(() => buildList(projectSettings.value?.serials_list));
const creativeOptions = computed<IFormSelectInputOption<number>[]>(() => buildList(projectSettings.value?.creatives_list));
const advertisingCompaniesOptions = computed<IFormSelectInputOption<number>[]>(() => buildList(projectSettings.value?.advertising_companies_list));
const conditionOptions = computed<IFormSelectInputOption<number>[]>(() => buildList(projectSettings.value?.all_condition));
const subaffiliateConditionOptions = computed<IFormSelectInputOption<number>[]>(() => buildList(projectSettings.value?.all_subaffiliate_condition));

const currencyOptions = computed<IFormSelectInputOption<string>[]>(() => {
  const val = projectSettings.value?.list_currency;
  if (!isNullOrUndefined(val)) {
    const arr: IFormSelectInputOption<string>[] = [];
    for (const [value, label] of Object.entries(val)) {
      arr.push({ value, label });
    }
    return arr;
  }

  return [];
});

const defaultLocaleOptions = computed(() => allLanguages.value.filter(
  (a) => interfaceLocaleSelection.value?.includes(a.value) || a.value === fallbackLocale.value,
));

const interfaceLanguagesFiltered = computed(() => (
  allLanguages.value.filter((a) => a.value !== projectSettings.value?.default_locale)
));

// generate dynamic creative domains fields
const domainFieldNames = computed<CreativeDomainNames[]>(() => {
  const count = config.value?.default_domains_count_for_creatives;
  if (isNumber(count)) {
    return (Array.from(Array.from({ length: count })).map((v, i) => (i === 0
      ? `${EProjectSettingsField.CREATIVE_DEFAULT_DOMAIN}`
      : `${EProjectSettingsField.CREATIVE_DEFAULT_DOMAIN}${i + 1}`)) as CreativeDomainNames[]);
  }

  return [];
});

const domainFields = computed<FormSchema<Record<CreativeDomainNames, string>>>(() => (
  domainFieldNames.value?.reduce((accumulator, fieldName) => {
    accumulator[fieldName] = {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, fieldName).value,
      value: projectSettings.value?.[fieldName],
    };

    return accumulator;
  }, {} as FormSchema<Record<CreativeDomainNames, string>>) || {}
));

function initForm(): void {
  if (!isNullOrUndefined(config.value) && !isNullOrUndefined(projectSettings.value)) {
    defaultLocaleSelection.value = projectSettings.value?.[EProjectSettingsField.DEFAULT_LOCALE];
    interfaceLocaleSelection.value = interfaceLanguages.value.map((a) => a.value);
    creativeLocaleSelection.value = creativeLanguages.value.map((a) => a.value);
  }
}

const formSchema = computed<FormSchema<IProjectSettingsFormPayload>>(() => ({
  [EProjectSettingsField.COMPANY_NAME]: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'COMPANY_NAME').value,
    value: projectSettings.value?.[EProjectSettingsField.COMPANY_NAME],
  },
  [EProjectSettingsField.MAX_TRACKERS]: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'MAX_TRACKERS').value,
    value: projectSettings.value?.[EProjectSettingsField.MAX_TRACKERS],
    useGrouping: false,
    maxFractionDigits: 0,
    min: 0,
  },
  ...domainFields.value,
  [EProjectSettingsField.DEFAULT_SERIAL_ID]: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'DEFAULT_SERIAL_ID').value,
    options: serialOptions.value,
    value: projectSettings.value?.[EProjectSettingsField.DEFAULT_SERIAL_ID],
  },
  [EProjectSettingsField.DEFAULT_CREATIVE_ID]: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'DEFAULT_CREATIVE_ID').value,
    options: creativeOptions.value,
    value: projectSettings.value?.[EProjectSettingsField.DEFAULT_CREATIVE_ID],
  },
  [EProjectSettingsField.DEFAULT_ADVERTISING_COMPANY_ID]: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'DEFAULT_ADVERTISING_COMPANY_ID').value,
    options: advertisingCompaniesOptions.value,
    value: projectSettings.value?.[EProjectSettingsField.DEFAULT_ADVERTISING_COMPANY_ID],
  },
  [EProjectSettingsField.CURRENT_DEFAULT_CONDITION_ID]: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'DEFAULT_CONDITION').value,
    options: conditionOptions.value,
    value: projectSettings.value?.[EProjectSettingsField.CURRENT_DEFAULT_CONDITION_ID],
  },
  [EProjectSettingsField.CURRENT_DEFAULT_SUBAFFILIATE_CONDITION_ID]: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(
      TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT,
      'DEFAULT_SUBAFFILIATE_CONDITION',
    ).value,
    options: subaffiliateConditionOptions.value,
    value: projectSettings.value?.[EProjectSettingsField.CURRENT_DEFAULT_SUBAFFILIATE_CONDITION_ID],
  },
  [EProjectSettingsField.TITLE]: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'TITLE').value,
    value: projectSettings.value?.[EProjectSettingsField.TITLE],
  },
  [EProjectSettingsField.CURRENCY]: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'CURRENCY').value,
    options: currencyOptions.value,
    value: currencyOptions.value.find((a) => a.label === config.value?.[EProjectSettingsField.CURRENCY])?.value,
  },
  [EProjectSettingsField.HEADER_COLOR]: {
    type: EFormInputTypes.COLOR,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'HEADER_COLOR').value,
    value: config.value?.[EProjectSettingsField.HEADER_COLOR],
  },
  [EProjectSettingsField.BUTTONS_COLOR]: {
    type: EFormInputTypes.COLOR,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'BUTTONS_COLOR').value,
    value: config.value?.[EProjectSettingsField.BUTTONS_COLOR],
  },
  [EProjectSettingsField.DEFAULT_LOCALE]: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'DEFAULT_LANGUAGE').value,
    options: defaultLocaleOptions.value,
    value: defaultLocaleSelection.value,
  },
  [EProjectSettingsField.INTERFACE_LOCALES]: {
    type: EFormInputTypes.MULTISELECT_BOX,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'PROJECT_LANGUAGES').value,
    options: interfaceLanguagesFiltered.value,
    value: interfaceLocaleSelection.value,
  },
  [EProjectSettingsField.CREATIVE_LOCALES]: {
    type: EFormInputTypes.MULTISELECT_BOX,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PROJECT, 'CREATIVE_LANGUAGES').value,
    options: allLanguages.value,
    value: creativeLocaleSelection.value,
  },
}));

function handleFormChange<
  T extends IProjectSettingsFormPayload,
  K extends FormFlattenKey<T>,
>(key: K, value: FormFlattenValue<T, K> | undefined, { values }: FormResult<T>): void {
  if (key === EProjectSettingsField.DEFAULT_LOCALE) {
    defaultLocaleSelection.value = values[EProjectSettingsField.DEFAULT_LOCALE];
  }

  if (key === EProjectSettingsField.INTERFACE_LOCALES) {
    interfaceLocaleSelection.value = values[EProjectSettingsField.INTERFACE_LOCALES];
  }

  if (key === EProjectSettingsField.CREATIVE_LOCALES) {
    creativeLocaleSelection.value = values[EProjectSettingsField.CREATIVE_LOCALES];
  }

  if (key === EProjectSettingsField.HEADER_COLOR || key === EProjectSettingsField.BUTTONS_COLOR) {
    updateThemeColors(values[EProjectSettingsField.HEADER_COLOR], values[EProjectSettingsField.BUTTONS_COLOR]);
  }
}

function handleSubmitForm(formValues: IProjectSettingsFormPayload): void {
  const { interfaceLocales, creativeLocales, ...settings } = formValues;
  void saveSettings({
    interfaceLocales: { project_languages: { locales: interfaceLocales } },
    creativeLocales: { project_languages: { locales: creativeLocales } },
    settings,
  });
}

onUnmounted(() => {
  updateThemeColors();
});

watch(interfaceLocaleSelection, () => {
  if (
    !defaultLocaleOptions.value
      .map((a) => a.value)
      .includes(defaultLocaleSelection.value || '')
      && fallbackLocale.value
  ) {
    // reset default locale if disabled in interface languages
    defaultLocaleSelection.value = fallbackLocale.value;
  }
}, { deep: true });

watchImmediate(projectSettings, (newValue) => {
  if (newValue)
    initForm();
}, { deep: true });
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsProjectPage'">
    <VLoader
      :loading="isLoading"
      min-height="70vh"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
        :is-loading="isLoadingSubmission"
        @change="handleFormChange"
        @submit="handleSubmitForm"
      />
    </VLoader>
  </VCard>
</template>
